import { useEffect, useRef, useState } from "react";
import {
  buildLiveReviewOwnershipSnapshot,
  buildReviewOwnershipApiPayload,
} from "../sections/review/KycReviewOwnershipSection";
import { assessUbosFromTree, applyUboAssessmentResultToOwnership } from "../../utils/uboAssessment";

function stableStringify(value) {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, val) => {
    if (val && typeof val === "object") {
      if (seen.has(val)) return undefined;
      seen.add(val);
      if (val instanceof Date) return val.toISOString();
      if (typeof val.toDate === "function") return val.toDate().toISOString();
      if (!Array.isArray(val)) {
        return Object.keys(val).sort().reduce((acc, k) => {
          acc[k] = val[k];
          return acc;
        }, {});
      }
    }
    return val;
  });
}

// What the UBO assessment depends on: a change here makes the stored results outdated.
export function ownershipAssessmentEditSignature(apiPayload) {
  const nodes = Array.isArray(apiPayload?.nodes) ? apiPayload.nodes : [];
  return stableStringify({
    initialAHType: apiPayload?.initialAHType || "",
    orgJurisdiction: apiPayload?.orgJurisdiction,
    nodeCount: nodes.length,
    nodes: nodes.map((node) => ({
      id: node?.ID || node?.id || "",
      name: node?.name || "",
      type: node?.type || "",
      parentIDs: Array.isArray(node?.parentIDs) ? node.parentIDs : [],
      percent: node?.percent === "" || node?.percent == null ? "" : String(node.percent),
      effectiveControl: node?.effectiveControl || null,
      nationality: node?.nationality || "",
      countryOfResidence: node?.countryOfResidence || "",
      dateOfBirth: node?.dateOfBirth || "",
      incorporationDate: node?.incorporationDate || "",
      // Trust/foundation parties and roles feed the CDB forms: their edits re-run the check too.
      roles: node?.roles || null,
      parties: Array.isArray(node?.parties) ? node.parties : [],
    })),
  });
}

// The UBO API payload for a form, from the ownership diagram as currently drawn.
export function liveOwnershipAssessmentPayload(form) {
  const ownership = form?.ownership && typeof form.ownership === "object" ? form.ownership : {};
  const accountHolderName = String(form?.customer?.name || "").trim() || "Account holder";
  const liveOwnershipGraph = buildLiveReviewOwnershipSnapshot({ ownership, accountHolderName });
  const withGraph = liveOwnershipGraph?.mermaid ? { ...ownership, assistantTree: liveOwnershipGraph } : ownership;
  return buildReviewOwnershipApiPayload({
    ownership: withGraph,
    accountHolderName,
    liveOwnershipGraph: withGraph.assistantTree,
  });
}

/**
 * Re-runs the UBO assessment about a second after the ownership changes, so the stored results
 * (meets threshold, qualified UBOs, documents required, CDB forms) never stay outdated. A result
 * is applied only if the ownership has not changed again meanwhile; a failure is retried on the
 * next change.
 */
export function useLiveUboAssessment({ form, setForm, orgJurisdiction, enabled = true }) {
  const requestRef = useRef(0);
  const signatureRef = useRef("");
  // Message of the last failed check (shown to the user), cleared by the next success.
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (!enabled || orgJurisdiction === null || orgJurisdiction === undefined) return undefined;

    const apiPayload = liveOwnershipAssessmentPayload(form);
    if (!apiPayload.initialAHType || !apiPayload.nodes.length) return undefined;

    const assessmentSignature = ownershipAssessmentEditSignature({ ...apiPayload, orgJurisdiction });
    if (assessmentSignature === signatureRef.current) return undefined;

    const timer = window.setTimeout(async () => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      try {
        const apiResult = await assessUbosFromTree(apiPayload.initialAHType, apiPayload.nodes, orgJurisdiction);
        if (requestRef.current !== requestId) return;
        setFailure("");

        setForm((prev) => {
          const currentPayload = liveOwnershipAssessmentPayload(prev);
          if (ownershipAssessmentEditSignature({ ...currentPayload, orgJurisdiction }) !== assessmentSignature) return prev;
          signatureRef.current = assessmentSignature;

          const currentOwnership = prev.ownership && typeof prev.ownership === "object" ? prev.ownership : {};
          const assessedOwnership = applyUboAssessmentResultToOwnership({
            ownership: currentOwnership,
            apiPayload: currentPayload,
            apiResult,
            signature: assessmentSignature,
          });
          if (stableStringify(currentOwnership) === stableStringify(assessedOwnership)) return prev;
          return { ...prev, ownership: assessedOwnership };
        });
      } catch (e) {
        if (requestRef.current === requestId) {
          signatureRef.current = "";
          setFailure(e?.message || "The UBO check failed.");
        }
        console.warn("UBO threshold live assessment failed:", e);
      }
    }, 1000);

    return () => {
      window.clearTimeout(timer);
      requestRef.current += 1;
    };
  }, [form, enabled, orgJurisdiction, setForm]);

  return { failure };
}

// Whether the UBO results stored on the form were computed for its current ownership.
export function storedUboAssessmentIsCurrent(form, orgJurisdiction) {
  const apiPayload = liveOwnershipAssessmentPayload(form);
  if (!apiPayload.initialAHType || !apiPayload.nodes.length) return true;
  const stored = form?.ownership?.uboAssessment?.signature || "";
  return !!stored && stored === ownershipAssessmentEditSignature({ ...apiPayload, orgJurisdiction });
}
